import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import apiService from '../services/api'

// Local formatters
const formatThaiCurrency = (amount: number) => {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0
  }).format(amount)
}

interface Course {
  id: number
  name: string
  price: number
  schedule: string
}

interface Enrollment {
  id: number
  student_name: string
  nickname: string
  month_year: string
  amount: number
  payment_status: string
  enrolled_at: string
}

export const EnrollmentPaymentPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  
  const course = location.state?.course as Course
  const enrollment = location.state?.enrollment as Enrollment
  const [paymentSlipFile, setPaymentSlipFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  useEffect(() => {
    // Redirect if no enrollment data
    if (!course || !enrollment) {
      navigate('/courses')
      return
    }
  }, [course, enrollment, navigate])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น')
        return
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        alert('ขนาดไฟล์ต้องไม่เกิน 5MB')
        return
      }
      setPaymentSlipFile(file)
    }
  }

  const handleUploadSlip = async () => {
    if (!paymentSlipFile || !enrollment) {
      return
    }

    try {
      setUploading(true)
      
      // In a real implementation, you'd upload to cloud storage first
      // For now, we'll simulate with a mock URL
      const mockSlipUrl = `https://cdn.krudream.com/slips/slip_${enrollment.id}_${Date.now()}.jpg`
      
      await apiService.uploadEnrollmentPaymentSlip(enrollment.id, mockSlipUrl)
      
      setUploadSuccess(true)
      
    } catch (error) {
      console.error('Failed to upload payment slip:', error)
      alert('ไม่สามารถอัปโหลดสลิปได้ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setUploading(false)
    }
  }

  if (!course || !enrollment) {
    return null // Will redirect via useEffect
  }

  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="อัปโหลดสลิปสำเร็จ" />
        
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              อัปโหลดสลิปการชำระเงินสำเร็จ!
            </h2>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              เจ้าหน้าที่จะตรวจสอบการชำระเงินของคุณภายใน 24 ชั่วโมง<br />
              เมื่อการชำระเงินได้รับการยืนยันแล้ว คุณจะได้รับการแจ้งเตือนผ่าน LINE
            </p>

            <div className="space-y-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                กลับสู่หน้าหลัก
              </button>
              
              <div className="text-sm text-gray-500">
                หรือ <button 
                  onClick={() => navigate('/courses')}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  ลงทะเบียนคอร์สเพิ่มเติม
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ชำระเงินค่าเรียน" 
        showBackButton
        onBack={() => navigate('/courses')}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Success Message */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-green-800 font-medium">ลงทะเบียนเรียนสำเร็จ!</span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            กรุณาชำระเงินค่าเรียนเพื่อยืนยันการลงทะเบียน
          </p>
        </div>

        {/* Enrollment Summary */}
        <DashboardCard title="รายละเอียดการลงทะเบียน" className="mb-6">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">นักเรียน:</span>
                <div className="font-medium">{enrollment.student_name}</div>
                <div className="text-sm text-gray-500">({enrollment.nickname})</div>
              </div>
              <div>
                <span className="text-sm text-gray-600">คอร์ส:</span>
                <div className="font-medium">{course.name}</div>
                <div className="text-sm text-gray-500">{course.schedule}</div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">เดือนที่ลงทะเบียน:</span>
                <span className="font-medium">{enrollment.month_year}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-gray-600">ค่าเรียน:</span>
                <span className="text-xl font-bold text-blue-600">
                  {formatThaiCurrency(enrollment.amount)}
                </span>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Payment Instructions */}
        <DashboardCard title="วิธีชำระเงิน" className="mb-6">
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">ข้อมูลบัญชีธนาคาร</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>ธนาคาร:</span>
                  <span className="font-medium">ไทยพาณิชย์</span>
                </div>
                <div className="flex justify-between">
                  <span>เลขที่บัญชี:</span>
                  <span className="font-medium">123-4-56789-0</span>
                </div>
                <div className="flex justify-between">
                  <span>ชื่อบัญชี:</span>
                  <span className="font-medium">โรงเรียนสอนพิเศษครูดรีม</span>
                </div>
                <div className="flex justify-between">
                  <span>จำนวนเงิน:</span>
                  <span className="font-bold text-blue-600">
                    {formatThaiCurrency(enrollment.amount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>วิธีการ:</strong></p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>โอนเงินตามจำนวนที่ระบุข้างต้น</li>
                <li>ถ่ายรูปสลิปการโอนเงิน</li>
                <li>อัปโหลดสลิปในระบบ</li>
                <li>รอการตรวจสอบจากเจ้าหน้าที่</li>
              </ol>
            </div>
          </div>
        </DashboardCard>

        {/* Upload Payment Slip */}
        <DashboardCard title="อัปโหลดสลิปการชำระเงิน">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เลือกไฟล์สลิป <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="mt-1 text-xs text-gray-500">
                รองรับไฟล์ JPG, PNG ขนาดไม่เกิน 5MB
              </div>
            </div>

            {paymentSlipFile && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{paymentSlipFile.name}</div>
                    <div className="text-xs text-gray-500">
                      {(paymentSlipFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    onClick={() => setPaymentSlipFile(null)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    ลบ
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={handleUploadSlip}
              disabled={!paymentSlipFile || uploading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                !paymentSlipFile || uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {uploading ? 'กำลังอัปโหลด...' : 'อัปโหลดสลิป'}
            </button>
          </div>
        </DashboardCard>

        {/* Skip Option */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-800 text-sm underline"
          >
            ข้ามไปก่อน (สามารถอัปโหลดภายหลังได้)
          </button>
        </div>

        {/* Important Notes */}
        <div className="mt-6 p-4 bg-red-50 rounded-lg">
          <h4 className="font-medium text-red-900 mb-2">ข้อมูลสำคัญ</h4>
          <ul className="text-sm text-red-800 space-y-1">
            <li>• <strong>กรุณาชำระเงินภายใน 24 ชั่วโมง</strong> หลังจากลงทะเบียน</li>
            <li>• การลงทะเบียนจะสมบูรณ์เมื่อเจ้าหน้าที่ตรวจสอบการชำระเงินแล้ว</li>
            <li>• หากไม่ชำระเงินตามเวลาที่กำหนด การลงทะเบียนจะถูกยกเลิก</li>
            <li>• สามารถดูประวัติการลงทะเบียนได้ในหน้าแดชบอร์ด</li>
          </ul>
        </div>
      </div>
    </div>
  )
}